import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { fetchMyEvents } from '@/services/eventService';
import { Event } from '@/lib/types';
import { useToast } from '@/hooks/use-toast';
import { EventSyncService } from '@/services/eventSyncService';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import MarketingBanners from '@/components/MarketingBanners';
import EventDetailsDrawer from '@/components/EventDetailsDrawer';
import { EventEditorModal, EventCreatorModal } from '@/components/admin/event-modals';
import DashboardHeader from '@/components/admin/dashboard/DashboardHeader';
import EventsTabs from '@/components/admin/dashboard/EventsTabs';
import OrganizerEventStatistics from '@/components/organizer/OrganizerEventStatistics';
import { UserRoles } from '@/contexts/auth/enums';

const OrganizerDashboard = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMarketingBanner, setShowMarketingBanner] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  const filteredEvents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return events;
    return events.filter((event) => {
      const haystack = [
        event.name,
        event.description,
        event.venue?.name,
        event.venue?.city,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [events, searchTerm]);

  // Esemény rendezési funkció
  const sortEvents = (eventsToSort: Event[]) => {
    return [...eventsToSort].sort((a, b) => {
      const dateA = new Date(a.dateStart);
      const dateB = new Date(b.dateStart);
      const now = new Date();

      // Ha mindkettő jövőbeli vagy mindkettő múltbeli
      if ((dateA > now && dateB > now) || (dateA <= now && dateB <= now)) {
        return dateA.getTime() - dateB.getTime(); // Időrendi sorrend
      }

      // Ha az egyik jövőbeli, a másik múltbeli
      return dateA > now ? -1 : 1; // Jövőbeli események előre
    });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      // Always use the ORGANIZER role to fetch only events owned by the current user
      const fetchedEvents = await fetchMyEvents(UserRoles.ORGANIZER);

      // Sync events to Supabase for analytics tracking
      if (user?.['@id']) {
        //    await EventSyncService.syncOrganizerEvents(fetchedEvents, user['@id']);
      }

      // Rendezzük az eseményeket
      const sortedEvents = sortEvents(fetchedEvents);

      setEvents(sortedEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      toast({
        title: "Hiba történt!",
        description: "Nem sikerült betölteni az eseményeket.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [toast]);

  if (!isAuthenticated || !user) {
    return null; // Will redirect due to the useEffect
  }

  const userFullName = `${user.userProfile.name.lastName} ${user.userProfile.name.firstName}`;

  const handleViewDetails = (event: Event) => {
    setSelectedEvent(event);
    setShowDetailsDrawer(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setShowEditorModal(true);
  };

  const handleCreateEvent = () => {
    setShowCreatorModal(true);
  };

  const handleEventUpdated = () => {
    // Újratöltjük az eseményeket és sorrendbe rendezzük őket
    loadEvents();
  };

  return (
    <div className="flex flex-col mt-32">
      <div className="mb-6">
        <DashboardHeader
          userFullName={userFullName}
          onLogout={logout}
          onCreateEvent={handleCreateEvent}
        />

        {showMarketingBanner && (
          <div className="mb-8">
            <MarketingBanners variant="dashboard" />
          </div>
        )}

        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Keresés a saját események között..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSearchTerm('')}
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
              aria-label="Keresés törlése"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <EventsTabs
          events={filteredEvents}
          isLoading={isLoading}
          onViewDetails={handleViewDetails}
          onEditEvent={handleEditEvent}
          onCreateEvent={handleCreateEvent}
        />

        <div className="mt-8">
          <OrganizerEventStatistics events={events} />
        </div>

        <EventDetailsDrawer
          event={selectedEvent}
          open={showDetailsDrawer}
          onClose={() => setShowDetailsDrawer(false)}
        />

        <EventEditorModal
          eventId={selectedEvent?.id || null}
          open={showEditorModal}
          onClose={() => setShowEditorModal(false)}
          onEventUpdated={handleEventUpdated}
        />

        <EventCreatorModal
          open={showCreatorModal}
          onClose={() => setShowCreatorModal(false)}
          onEventCreated={handleEventUpdated}
        />
      </div>
    </div>
  );
};

export default OrganizerDashboard;
